import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, User, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginPage = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      const checkToken = async () => {
        try {
          const response = await fetch(`${API_URL}/api/admin/verify?token=${token}`);
          if (response.ok) {
            navigate('/admin');
          } else {
            localStorage.removeItem('admin_token');
          }
        } catch (err) {
          localStorage.removeItem('admin_token');
        }
      };
      checkToken();
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Giriş başarısız');
      }

      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_username', data.username);
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const texts = {
    tr: {
      title: 'Admin Girişi',
      subtitle: 'Yönetim paneline erişmek için giriş yapın',
      username: 'Kullanıcı Adı',
      password: 'Şifre',
      login: 'Giriş Yap',
      back: 'Ana Sayfaya Dön',
    },
    en: {
      title: 'Admin Login',
      subtitle: 'Sign in to access the admin panel',
      username: 'Username',
      password: 'Password',
      login: 'Sign In',
      back: 'Back to Home',
    },
  };

  const t = texts[language];

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_platform-control-9/artifacts/v13sl9x3_Ads%C4%B1z%20tasar%C4%B1m%20%2815%29.png"
              alt="AgrosTech"
              className="w-12 h-12 object-contain dark:drop-shadow-[0_0_8px_rgba(255,255,255,0.25)]"
            />
            <span className="text-2xl font-bold tracking-tight">
              <span className="text-slate-900 dark:text-white">Agros</span>
              <span className="text-indigo-600 dark:text-indigo-400">Tech</span>
            </span>
          </a>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t.title}</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">{t.subtitle}</p>
        </div>

        {/* Login Form */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-xl border border-slate-200 dark:border-slate-800">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4" strokeWidth={1.5} />
                {t.username}
              </Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="admin"
                required
                className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                data-testid="login-username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Lock className="w-4 h-4" strokeWidth={1.5} />
                {t.password}
              </Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="••••••••"
                required
                className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3"
              data-testid="login-submit"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t.login
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a 
              href="/" 
              className="text-sm text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              ← {t.back}
            </a>
          </div>
        </div>

        {/* Hint removed */}
      </div>
    </div>
  );
};
